/**
 * Converts the starting month of the given period (e.g. 'APR-JUL') to
 * a month (e.g. 4) and day (e.g. 1).
 *
 * examples:
 *  convertPeriodToMonthsAndDays('JAN-SEP') -> [[1, 1], [9, 30]]
 *  convertPeriodToMonthsAndDays('JUN16-JUL') -> [[6, 16], [7, 31]]
 *  convertPeriodToMonthsAndDays('APR-JUL') -> [[4, 1], [7, 31]]
 *
 * @param {string} period
 */
export function convertPeriodToMonthsAndDays(period) {
    const monthStartMap = {
        JAN: [1, 1],
        FEB: [2, 1],
        MAR: [3, 1],
        APR: [4, 1],
        MAY: [5, 1],
        JUN: [6, 1],
        JUL: [7, 1],
        AUG: [8, 1],
        SEP: [9, 1],
        J16: [1, 16],
        F16: [2, 16],
        M16: [3, 16],
        A16: [4, 16],
        MAY16: [5, 16],
        JUN16: [6, 16],
    };
    const monthEndMap = {
        JAN: [1, 31],
        FEB: [2, 28],
        MAR: [3, 31],
        APR: [4, 30],
        MAY: [5, 31],
        JUN: [6, 30],
        JUL: [7, 31],
        AUG: [8, 31],
        SEP: [9, 30],
        J16: [1, 16],
        F16: [2, 16],
        M16: [3, 16],
        A16: [4, 16],
        MAY16: [5, 16],
        JUN16: [6, 16],
    };

    // Catch the case with single month periods (e.g. 'JAN' should return [[1,1], [1,31]])
    if (!period.includes("-")) {
        period = `${period}-${period}`;
    }

    const [pStart, pEnd] = period.split("-").map((p) => p.trim());
    return [monthStartMap[pStart], monthEndMap[pEnd]];
}

/**
 *
 * Given a javascript date (e.g. new Date('2021-04-12')), returns true
 * if the date is within the given period (e.g. APR-JUL) for the given
 * year (e.g. 2021). Otherwise, returns false.
 *
 * examples:
 *  isDateInPeriod(new Date('2001-05-23'), 'JAN-SEP', 2001) -> true
 *  isDateInPeriod(new Date('2001-05-23'), 'JAN-SEP', 2003) -> false
 *  isDateInPeriod(new Date('2001-05-23'), 'JUN-SEP', 2001) -> false
 *  isDateInPeriod(new Date('2001-01-21'), 'APR-JUL', 2001) -> false
 *
 * @param {Date} date
 * @param {String} period
 * @param {integer} year
 * @returns {Boolean}
 */
export function isDateInPeriod(date, period, year) {
    const convertedPeriod = convertPeriodToMonthsAndDays(period);
    const [pStartMonth, pStartDay] = convertedPeriod[0];
    const [pEndMonth, pEndDay] = convertedPeriod[1];
    const periodStartDate = new Date(
        `${year}-${pStartMonth.toString().padStart(2, "0")}-${pStartDay.toString().padStart(2, "0")}`,
    );
    const periodEndDate = new Date(
        `${year}-${pEndMonth.toString().padStart(2, "0")}-${pEndDay.toString().padStart(2, "0")}`,
    );

    return date >= periodStartDate && date <= periodEndDate;
}

/**
 * Converts a given month and day to the corresponding period label.
 *
 * example:
 *   convertMonthAndDayToPeriodLabel(5, 1) -> "MAY"
 *   convertMonthAndDayToPeriodLabel(3,15) -> "M16"
 *   convertMonthAndDayToPeriodLabel(6,16) -> "JUN16"
 *
 * Also works with 'end-of-periods'
 *   convertMonthAndDayToPeriodLabel(7,31) -> "JUL"
 *
 *
 * @param {integer} month
 * @param {integer} day
 * @returns {string} the period label (e.g. JAN, or MAY16)
 */
export function convertMonthAndDayToPeriodLabel(month, day) {
    // Check day and month validity
    if (![1, 15, 16, 28, 29, 30, 31].includes(day)) {
        throw Error("convertMonthAndDayToPeriodLabel(...) expects the day to be 1, 15, 16, 28, 29, 30, or 31.");
    }
    if (month < 1 || month > 12) {
        throw Error("convertMonthAndDayToPeriodLabel(...) expects the month to be between 1 and 12");
    }
    if (month > 6 && [15, 16].includes(day)) {
        throw Error("convertMonthAndDayToPeriodLabel(...) cannot create mid-month periods after June.");
    }

    const monthNames = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
    const midMonthNames = ["J16", "F16", "M16", "A16", "MAY16", "JUN16"];

    if (day === 1) {
        return monthNames[month - 1];
    } else if ([15, 16].includes(day)) {
        return midMonthNames[month - 1];
    } else if (day === 28 || day === 29) {
        if (month !== 2) throw Error("convertMonthAndDayToPeriodLabel(...): if day is 28 or 29, month must be 2");

        return "FEB";
    } else if (day === 30) {
        if (![4, 6, 9, 11].includes(month))
            throw Error("convertMonthAndDayToPeriodLabel(...): if day is 30, month must be 4, 6, 9, or 11");

        return monthNames[month - 1];
    } else if (day === 31) {
        if (![1, 3, 5, 7, 8, 10, 12].includes(month))
            throw Error("convertMonthAndDayToPeriodLabel(...): if day is 31, month must be 1,3,5,7,8,10,12");

        return monthNames[month - 1];
    }
}

// Forecast Utilities
/**
 * Returns the sort order for two given forecast periods. That is,
 * returns -1 if periodA is before periodB, and 1 for periodB before periodA.
 *
 * @param {String} periodA - first period, e.g. APR-JUL
 * @param {String} periodB - second period, e.g. MAY-JUL
 * @returns {integer} - sorting order.
 */
export function sortFcstPeriods(periodA, periodB) {
    // Overall order of forecast periods.
    const fcstPeriodOrder = [
        "JAN",
        "J16",
        "FEB",
        "F16",
        "MAR",
        "M16",
        "APR",
        "A16",
        "MAY",
        "MAY16",
        "JUN",
        "JUN16",
        "JUL",
        "AUG",
        "SEP",
        "HIGH",
    ];

    const [startA, endA] = periodA.split("-").map((m) => fcstPeriodOrder.indexOf(m));
    const [startB, endB] = periodB.split("-").map((m) => fcstPeriodOrder.indexOf(m));

    if (startA < startB) {
        return -1;
    }
    if (startA > startB) {
        return 1;
    }
    if (startA === startB) {
        if (endA < endB) return -1;
        if (endA > endB) return 1;
    }
    return 0;
}
/**
 * Checks if 2 arrays (a and b) are exactly equivalent...
 */
export function arraysEqual(a, b) {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; ++i) {
        if (a[i] !== b[i]) return false;
    }
    return true;
}

/**
 * Determines whether a candidate forecast period is a valid period to
 * be included in a mixed past target period. For example: the period
 * 'MAY-JUL' would be a valid period for the target period 'APR-JUL', and
 * therefore would return true.
 * @param {*} candidatePeriod
 * @param {*} targetPeriod
 * @returns {boolean} = true if the candidate period falls withing the
 *          target period. otherwise, false.
 */
export function isValidMixedPastPeriod(candidatePeriod, targetPeriod) {
    const fcstPeriodOrder = [
        "JAN",
        "J16",
        "FEB",
        "F16",
        "MAR",
        "M16",
        "APR",
        "A16",
        "MAY",
        "MAY16",
        "JUN",
        "JUN16",
        "JUL",
        "JUL16",
        "AUG",
        "AUG16",
        "SEP",
        "HIGH",
    ];

    if (candidatePeriod === targetPeriod) {
        return true;
    }

    const [candidatePeriodStart, candidatePeriodEnd] = convertPeriodToMonthsAndDays(candidatePeriod);
    const [targetPeriodStart, targetPeriodEnd] = convertPeriodToMonthsAndDays(targetPeriod);
    const candidatePeriodStartLbl = convertMonthAndDayToPeriodLabel(...candidatePeriodStart);
    const targetPeriodStartLbl = convertMonthAndDayToPeriodLabel(...targetPeriodStart);

    if (arraysEqual(candidatePeriodEnd, targetPeriodEnd)) {
        if (fcstPeriodOrder.indexOf(candidatePeriodStartLbl) >= fcstPeriodOrder.indexOf(targetPeriodStartLbl)) {
            return true;
        }
    }

    return false;
}
